import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { COMMAND_CATEGORIES, formatCommandSlash } from "../data/commands";
import { COMMUNITY_INVITE, COMMUNITY_NAME } from "../data/links";

const API_BASE = process.env.REACT_APP_API_BASE;

function PrefixCode({ prefix, command }) {
  if (!prefix) return null;
  return <code>{prefix}{command}</code>;
}

export default function HelpPage({ guilds = [] }) {
  const [activeCategory, setActiveCategory] = useState("leveling");
  const [search, setSearch] = useState("");
  const botGuilds = useMemo(
    () => (guilds || []).filter((g) => g.hasBot),
    [guilds],
  );
  const [guildId, setGuildId] = useState("");
  const [prefix, setPrefix] = useState(null);
  const [prefixLoading, setPrefixLoading] = useState(false);

  useEffect(() => {
    if (!botGuilds.length) {
      setGuildId("");
      setPrefix(null);
      return;
    }
    setGuildId((current) =>
      current && botGuilds.some((g) => g.id === current)
        ? current
        : botGuilds[0].id,
    );
  }, [botGuilds]);

  useEffect(() => {
    if (!guildId) {
      setPrefix(null);
      return;
    }

    let cancelled = false;
    setPrefixLoading(true);

    fetch(`${API_BASE}/settings/${guildId}`)
      .then((res) => res.json())
      .then((settings) => {
        if (cancelled) return;
        const raw = settings?.commandPrefix;
        const next =
          typeof raw === "string" && raw.trim() ? raw.trim() : null;
        setPrefix(next);
      })
      .catch(() => {
        if (!cancelled) setPrefix(null);
      })
      .finally(() => {
        if (!cancelled) setPrefixLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [guildId]);

  const filtered = COMMAND_CATEGORIES.map((cat) => ({
    ...cat,
    commands: cat.commands.filter(
      (cmd) =>
        cmd.name.includes(search.toLowerCase()) ||
        cmd.desc.toLowerCase().includes(search.toLowerCase()),
    ),
  })).filter((cat) => cat.commands.length > 0 || !search);

  const active =
    filtered.find((c) => c.id === activeCategory) || filtered[0];

  const sampleCmd = active?.commands[0]?.name || "hug";
  const selectedGuild = botGuilds.find((g) => g.id === guildId);

  return (
    <div className="help-page">
      <Link to="/" className="back-link">
        <span>←</span> Back to home
      </Link>

      <header className="help-header">
        <h1>Command Reference</h1>
        <p>
          Browse commands by category. For exact usage in Discord, use{" "}
          <code>/help</code>
          {prefix ? (
            <>
              {" "}
              or <PrefixCode prefix={prefix} command={`help ${sampleCmd}`} />
            </>
          ) : (
            <> with the command option (e.g. <code>hug</code>)</>
          )}
          .
        </p>
        <p className="help-support-line">
          Need help setting Kami up?{" "}
          <a href={COMMUNITY_INVITE} target="_blank" rel="noreferrer">
            Join {COMMUNITY_NAME}
          </a>{" "}
          for community chat and support.
        </p>
      </header>

      {botGuilds.length > 0 && (
        <div className="help-prefix-picker">
          <label htmlFor="help-server-prefix">Examples for server</label>
          <select
            id="help-server-prefix"
            className="settings-input"
            value={guildId}
            onChange={(e) => setGuildId(e.target.value)}
          >
            {botGuilds.map((g) => (
              <option key={g.id} value={g.id}>
                {g.name}
              </option>
            ))}
          </select>
          <p className="help-prefix-status">
            {prefixLoading
              ? "Loading prefix…"
              : prefix
                ? (
                  <>
                    Chat prefix for {selectedGuild?.name || "this server"}:{" "}
                    <code>{prefix}</code>
                  </>
                ) : (
                  <>
                    {selectedGuild?.name || "This server"} has no chat prefix
                    set - slash commands only. You can add one under{" "}
                    <Link to={`/manage/${guildId}`}>
                      System Channels
                    </Link>
                    .
                  </>
                )}
          </p>
        </div>
      )}

      <section className="help-guide">
        <h2>How to run commands</h2>
        <div className="help-guide-grid">
          <div>
            <h3>Slash</h3>
            <p>
              Most commands live in groups: <code>/fun hug</code>,{" "}
              <code>/mod ban</code>, <code>/util suggest</code>,{" "}
              <code>/level daily</code>, <code>/music play</code>.
            </p>
            <p>
              Always top-level: <code>/help</code>, <code>/settings</code>,{" "}
              <code>/website</code>.
            </p>
          </div>
          <div>
            <h3>Prefix</h3>
            {prefix ? (
              <>
                <p>
                  This server&apos;s prefix is <code>{prefix}</code>. Use the
                  short name - no group:{" "}
                  <PrefixCode prefix={prefix} command="hug" />,{" "}
                  <PrefixCode prefix={prefix} command="ban" />,{" "}
                  <PrefixCode prefix={prefix} command="suggest" />.
                </p>
              </>
            ) : (
              <>
                <p>
                  Prefix is optional and set per server (dashboard → System
                  Channels). Until one is set, use slash commands only.
                </p>
                <p>
                  {botGuilds.length === 0
                    ? "Log in and pick a server above to see that server's prefix in these examples."
                    : "After you set a prefix, examples on this page update automatically."}
                </p>
              </>
            )}
          </div>
          <div>
            <h3>Command help</h3>
            <p>Get usage for any command in Discord:</p>
            <p>
              <code>/help</code> → fill in <code>command</code> (e.g.{" "}
              <code>hug</code> or <code>ban</code>)
            </p>
            {prefix ? (
              <p>
                Or: <PrefixCode prefix={prefix} command="help hug" />,{" "}
                <PrefixCode prefix={prefix} command="help ban" />
              </p>
            ) : (
              <p>
                Prefer slash until a prefix is set. Then the same lookup works as{" "}
                <code>[prefix]help hug</code> using your server&apos;s prefix.
              </p>
            )}
            <p>
              Plain <code>/help</code>
              {prefix ? (
                <>
                  {" "}
                  or <PrefixCode prefix={prefix} command="help" />
                </>
              ) : null}{" "}
              opens the category browser.
            </p>
          </div>
        </div>
      </section>

      <input
        type="search"
        className="settings-input help-search"
        placeholder="Search commands..."
        value={search}
        onChange={(e) => setSearch(e.target.value.toLowerCase())}
      />

      <div className="help-layout">
        <nav className="help-nav">
          {filtered.map((cat) => (
            <button
              key={cat.id}
              type="button"
              className={`help-nav-item ${active?.id === cat.id ? "active" : ""}`}
              onClick={() => setActiveCategory(cat.id)}
            >
              <span>{cat.emoji}</span>
              <span>{cat.label}</span>
              <span className="help-count">{cat.commands.length}</span>
            </button>
          ))}
        </nav>

        <div className="help-content">
          {active && (
            <>
              <h2>
                {active.emoji} {active.label}
              </h2>
              <p className="help-category-desc">{active.description}</p>
              <p className="help-category-hint">
                Need details? In Discord run{" "}
                <code>/help</code> and set command to{" "}
                <code>{sampleCmd}</code>
                {prefix ? (
                  <>
                    {" "}
                    or <PrefixCode prefix={prefix} command={`help ${sampleCmd}`} />
                  </>
                ) : null}
                .
              </p>
              <div className="help-command-list">
                {active.commands.map((cmd) => (
                  <div key={cmd.name} className="help-command">
                    <code>{formatCommandSlash(cmd, active.id)}</code>
                    <span>{cmd.desc}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      <section className="help-tip">
        <h3>Quick reminder</h3>
        <p>
          Slash uses groups (<code>/mod ban</code>).
          {prefix ? (
            <>
              {" "}
              Prefix uses the short name (
              <PrefixCode prefix={prefix} command="ban" />
              ). For usage help:{" "}
              <PrefixCode prefix={prefix} command="help ban" /> or{" "}
              <code>/help</code> with command <code>ban</code>.
            </>
          ) : (
            <>
              {" "}
              Prefix is off until you set one in System Channels - until then
              stick to slash, and use <code>/help</code> with the command option
              for usage details.
            </>
          )}
        </p>
      </section>
    </div>
  );
}
